import React, { useContext } from 'react';
import { DndContext, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CourseEditorContext, COURSE_MODES } from '../../../../../contexts/CourseEditorContext';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { QuestionSection } from './QuestionSection/QuestionSection';
import { QuestionUserView } from './QuestionSection/QuestionUserView';

// Custom sensor that checks if resize is in progress
class CustomPointerSensor extends PointerSensor {
    static activators = [
        {
            eventName: 'onPointerDown',
            handler: ({ nativeEvent: event }) => {
                // Don't activate if resize is in progress
                if (window.resizeInProgress) {
                    return false;
                }

                // If not resizing, use the default PointerSensor activation logic
                if (event.button !== 0 || event.isPrimary === false) {
                    return false;
                }

                return true;
            }
        }
    ];
}

/**
 * EditorContent - Renders the canvas area with drag-and-drop components and background media.
 * Gets mode, selectedSection, signDocId, isTemplateMode, templateId, isFullScreen, canvasScale,
 * and selectedComponentId from CourseEditorContext.
 *
 * @param {Object} props - Component props
 * @param {Array} props.components - Array of component objects to render
 * @param {Function} props.onVideoComplete - Callback when video playback completes
 * @param {boolean} props.courseCompleted - Whether the course is completed
 * @param {Function} props.handleDragStart - Drag start handler
 * @param {Function} props.handleDragEnd - Drag end handler
 * @param {Function} props.handleCanvasClick - Canvas click handler
 * @param {Function} props.handleCanvasDoubleClick - Canvas double-click handler
 * @param {Function} props.handleSelectComponent - Component selection handler
 * @param {Function} props.handleContentChange - Content change handler
 * @param {Function} props.handleRemoveComponent - Component removal handler
 * @param {Function} props.handleDuplicateComponent - Component duplication handler
 * @param {Function} props.handleResize - Component resize handler
 * @param {Function} props.handleOpenEditorDrawer - Open editor drawer handler
 * @param {React.RefObject} props.canvasWrapperRef - Ref for canvas wrapper element
 * @param {React.RefObject} props.componentsContainerRef - Ref for components container
 * @param {boolean} props.showComponentAnimations - Whether to show component animations
 * @param {boolean} props.isSequentialAnimationActive - Whether sequential animations are active
 * @param {number} props.currentAnimatingComponentIndex - Current animating component index
 * @param {Set} props.animatedComponents - Set of animated component IDs
 * @param {Function} props.handleAnimationComplete - Animation completion handler
 * @param {React.Component} props.DraggableComponent - DraggableComponent to render
 * @param {boolean} [props.isPreview=false] - Whether in preview mode
 */
export const EditorContent = ({
    components,
    onVideoComplete,
    courseCompleted = false,
    handleDragStart,
    handleDragEnd,
    handleCanvasClick,
    handleCanvasDoubleClick,
    handleSelectComponent,
    handleContentChange,
    handleRemoveComponent,
    handleDuplicateComponent,
    handleResize,
    handleOpenEditorDrawer,
    canvasWrapperRef,
    componentsContainerRef,
    showComponentAnimations,
    isSequentialAnimationActive,
    currentAnimatingComponentIndex,
    animatedComponents,
    handleAnimationComplete,
    DraggableComponent,
    isPreview = false
}) => {
    const { i18n } = useContext(I18nReactContext);

    // Get all state from context
    const {
        mode = COURSE_MODES.EDIT,
        selectedSection,
        isFullScreen = false,
        setFullScreen
    } = useContext(CourseEditorContext) || {};

    // Create toggle fullscreen function
    const toggleFullscreen = () => {
        if (setFullScreen) {
            setFullScreen(!isFullScreen);
        }
    };

    // Configure sensors for drag and drop
    const sensors = useSensors(
        useSensor(CustomPointerSensor, {
            activationConstraint: { distance: 8 }
        })
    );

    return (
        <div className="editor-content">
            <div ref={canvasWrapperRef} className="canvas-wrapper">
                <DndContext 
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <div className="canvas-scale-container">
                        <div 
                            ref={componentsContainerRef}
                            className={`components-container canvas-mode ${selectedSection?.backgroundImage ? 'has-background-image' : ''} ${selectedSection?.backgroundVideo ? 'has-background-video' : ''}`}
                            onClick={handleCanvasClick}
                            onDoubleClick={handleCanvasDoubleClick}
                            dir={selectedSection?.question ? "" : "ltr"}
                            style={(selectedSection?.backgroundImage || selectedSection?.backgroundVideo) ? {
                                backgroundColor: (selectedSection.backgroundImage?.backgroundColor || selectedSection.backgroundVideo?.backgroundColor) || 'transparent'
                            } : {}}
                        >
                            {selectedSection?.backgroundImage && (
                                <div 
                                    className="background-image-container"
                                    style={{
                                        backgroundImage: `url(${selectedSection.backgroundImage.signedUrl})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        opacity: selectedSection.backgroundImage.opacity || 1,
                                        borderRadius: selectedSection.backgroundImage.borderRadius ? 
                                            `${selectedSection.backgroundImage.borderRadius.topLeft || 0}px ${selectedSection.backgroundImage.borderRadius.topRight || 0}px ${selectedSection.backgroundImage.borderRadius.bottomRight || 0}px ${selectedSection.backgroundImage.borderRadius.bottomLeft || 0}px` : 
                                            '0px'
                                    }}
                                />
                            )}
                            {selectedSection?.backgroundVideo && selectedSection?.backgroundVideo?.signedUrl && (
                                <div className="background-video-container">
                                    <video
                                        src={selectedSection.backgroundVideo.signedUrl}
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            opacity: selectedSection.backgroundVideo.opacity || 1,
                                            borderRadius: selectedSection.backgroundVideo.borderRadius ?
                                                `${selectedSection.backgroundVideo.borderRadius.topLeft || 0}px ${selectedSection.backgroundVideo.borderRadius.topRight || 0}px ${selectedSection.backgroundVideo.borderRadius.bottomRight || 0}px ${selectedSection.backgroundVideo.borderRadius.bottomLeft || 0}px` :
                                                '0px'
                                        }}
                                    />
                                </div>
                            )}
                            {selectedSection?.question && mode === COURSE_MODES.EDIT && (
                                <QuestionSection sectionId={selectedSection.id} />
                            )}
                            {selectedSection?.question && (mode === COURSE_MODES.ADMIN_VIEW || mode === COURSE_MODES.USER_VIEW) && (
                                <QuestionUserView sectionId={selectedSection.id} />
                            )}
                            {components.length === 0 && !selectedSection?.question ? (
                                <div className="empty-state">
                                    <p>{i18n.translate('weezbeeCourse.editor.chapterEditor.emptyState')}</p>
                                </div>
                            ) : (
                                <>
                                    {components.map((component, index) => {
                                        // Determine if this component should be visible during sequential animations
                                        const shouldShowComponent = !isSequentialAnimationActive || 
                                                                   index <= currentAnimatingComponentIndex || 
                                                                   animatedComponents.has(component.id);

                                        return (
                                            <DraggableComponent
                                                key={component.id}
                                                id={component.id}
                                                component={component}
                                                onSelect={handleSelectComponent}
                                                onContentChange={handleContentChange}
                                                onDelete={handleRemoveComponent}
                                                onDuplicate={handleDuplicateComponent}
                                                onResize={handleResize}
                                                onVideoComplete={onVideoComplete}
                                                courseCompleted={courseCompleted}
                                                onOpenEditorDrawer={handleOpenEditorDrawer}
                                                animationDelay={index * 200} // 200ms delay between each component
                                                isShowingAIAnimations={showComponentAnimations}
                                                onAnimationComplete={handleAnimationComplete}
                                                isSequentialAnimationActive={isSequentialAnimationActive}
                                                shouldShowComponent={shouldShowComponent}
                                            />
                                        );
                                    })}
                                </>
                            )}

                            {/* Fullscreen button - only shown in view modes and not in preview mode */}
                            {(mode === COURSE_MODES.ADMIN_VIEW || mode === COURSE_MODES.USER_VIEW) && !isPreview && (
                                <div className="fullscreen-button-container">
                                    <div
                                        className="fullscreen-icon-wrapper"
                                        onClick={toggleFullscreen}
                                    >
                                        {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </DndContext>
            </div>
        </div>
    );
};
